import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import toast from "react-hot-toast";

import { Button } from "../../../../_helpers/components/Button";
import { Toggle } from "../../../../_helpers/components/Forms/ToggleButton";
import { InputForm } from "../../../../_helpers/components/Forms/InputLabel";
import { DefaultDropdown } from "../../../../_helpers/components/Forms/Dropdown";
import { Loader } from "../../../../_helpers/components/Forms/Loader";
import { MultiSelect } from "../../../../_helpers/components/Forms/MultiSelect";
import {
    getUserCheckingRules,
    updateUserCheckingRules,
    attachUserBadgeuse,
    detachUserBadgeuse,
} from "../../../../_api/Admin";
import { getBadgeusesEstablishments } from "../../../../_api/Badgeuse";

const RULE_CODES = {
    START_MIN: "start_min",
    END_MAX: "end_max",
    MAX_AMPLITUDE: "max_amplitude",
    MEAL_BREAK: "meal_break",
};

const MODE_OPTIONS = [
    { value: "fixed", label: "Heure fixe" },
    { value: "provisional", label: "Identique au planning prévisionnel" },
];

const MEAL_MODE_OPTIONS = [
    { value: "fixed_window", label: "Fenêtre horaire fixe" },
    { value: "provisional", label: "Identique à la pause du planning prévisionnel" },
];

const BADGEUSE_TYPE_LABELS = {
    PHYSICAL: "Physique",
    WEB: "Internet",
    MOBILE: "Mobile",
};

const badgeuseOptionLabel = (badgeuse) =>
    `${badgeuse.name} — ${BADGEUSE_TYPE_LABELS[badgeuse.type] ?? badgeuse.type}`;

const toBadgeuseOption = (badgeuse) => ({
    value: badgeuse.id,
    label: badgeuseOptionLabel(badgeuse),
    id: badgeuse.id,
});

/** Convertit des minutes (stockage DB) en chaîne hh:mm pour l'affichage. */
const minutesToHhMm = (totalMinutes) => {
    const m = Math.max(0, Math.floor(Number(totalMinutes) || 0));
    const h = Math.floor(m / 60);
    const min = m % 60;
    return `${String(h).padStart(2, "0")}:${String(min).padStart(2, "0")}`;
};

/** Convertit une chaîne hh:mm (saisie) en minutes pour l'enregistrement. */
const hhMmToMinutes = (hhmm) => {
    if (!hhmm || typeof hhmm !== "string") return 0;
    const parts = hhmm.split(":");
    if (parts.length !== 2) return 0;
    const h = parseInt(parts[0], 10);
    const min = parseInt(parts[1], 10);
    if (Number.isNaN(h) || Number.isNaN(min) || min < 0 || min >= 60) return 0;
    return h * 60 + min;
};



const PointageTab = (props) => {
    const [user] = useOutletContext();
    const editRight =
        user?.policy?.rights?.COMPANY?.EMPLOYEES?.sub_menu?.CHECKING_RULES
            ?.features?.edit_checking_rules ?? true;

    const { profile, refreshProfil } = props;

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [rules, setRules] = useState(null);
    const [availableBadgeuses, setAvailableBadgeuses] = useState([]);
    const [badgeusesSelected, setBadgeusesSelected] = useState([]);
    const [isActiveBadgeuses, setIsActiveBadgeuses] = useState(null);

    useEffect(() => {
        if (!profile?.id) return;
        setLoading(true);
        getUserCheckingRules(profile.id)
            .then((resp) => {
                setRules(resp.data?.data ?? resp.data);
            })
            .catch(() => toast.error("Impossible de charger les règles."))
            .finally(() => setLoading(false));
    }, [profile?.id]);

    useEffect(() => {
        if (!profile?.establishments?.length) {
            setAvailableBadgeuses([]);
            setBadgeusesSelected([]);
            return;
        }

        setBadgeusesSelected(
            (profile.badgeuses ?? []).map((badgeuse) => toBadgeuseOption(badgeuse))
        );

        Promise.all(
            profile.establishments.map((establishment) =>
                getBadgeusesEstablishments(establishment.id).then((resp) => resp.data ?? [])
            )
        )
            .then((results) => {
                const merged = new Map();
                results.flat().forEach((badgeuse) => {
                    merged.set(badgeuse.id, toBadgeuseOption(badgeuse));
                });
                setAvailableBadgeuses(Array.from(merged.values()));
            })
            .catch(() => toast.error("Impossible de charger les badgeuses."));
    }, [profile]);

    const updateRule = (code, patch) => {
        setRules((prev) => ({
            ...prev,
            [code]: {
                ...prev[code],
                ...patch,
                params: {
                    ...(prev?.[code]?.params || {}),
                    ...(patch.params || {}),
                },
            },
        }));
    };

    const handleSave = () => {
        if (!rules) return;
        setSaving(true);
        updateUserCheckingRules(profile.id, rules)
            .then((resp) => {
                setRules(resp.data?.data ?? rules);
                toast.success("Règles de pointage enregistrées.");
            })
            .catch(() => toast.error("Erreur lors de l'enregistrement."))
            .finally(() => setSaving(false));
    };

    if (loading || !rules) {
        return <Loader text="Chargement des règles de pointage..." />;
    }

    const startMin = rules[RULE_CODES.START_MIN] || {};
    const endMax = rules[RULE_CODES.END_MAX] || {};
    const amplitude = rules[RULE_CODES.MAX_AMPLITUDE] || {};
    const mealBreak = rules[RULE_CODES.MEAL_BREAK] || {};

    const Section = ({ title, description, ruleCode, ruleValue, children }) => (
        <div className="mb-5 pb-5 border-b border-gray-100">
            <div className="flex items-center mb-4">
                <div className="w-1.5 min-w-1.5 h-6 bg-red-500 rounded-r mr-3"></div>
                {ruleCode && (
                    <div className="mx-2">
                        <Toggle
                            enabled={!!ruleValue.enabled}
                            setEnabled={() =>
                                updateRule(ruleCode, {
                                    enabled: !ruleValue.enabled,
                                })
                            }
                            name=" "
                        />
                    </div>
                )}
                <span className="mx-2 text-xs font-semibold text-gray-700 uppercase tracking-wide">{title}</span>
                {description && (
                    <span className="text-sm text-gray-500 mt-1 mb-2">{description}</span>
                )}
            </div>

            {ruleValue.enabled && (
                children
            )}
        </div>
    );

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <div className="mb-5 pb-5 border-b border-gray-100">
                    <div className="flex items-center mb-4">
                        <div className="w-1.5 min-w-1.5 h-6 bg-red-500 rounded-r mr-3"></div>
                        <span className="mx-2 text-xs font-semibold text-gray-700 uppercase tracking-wide">
                            Badgeuses
                        </span>
                        <span className="text-sm text-gray-500 mt-1 mb-2">
                            Associez les badgeuses sur lesquelles l&apos;utilisateur peut pointer.
                        </span>
                    </div>
                    {editRight ? (
                        <MultiSelect
                            name={"Badgeuses"}
                            selections={badgeusesSelected}
                            options={availableBadgeuses.filter(
                                (badgeuse) =>
                                    !badgeusesSelected.some(
                                        (selected) => selected.value === badgeuse.value
                                    )
                            )}
                            value={""}
                            isActive={isActiveBadgeuses}
                            setActive={(val) => setIsActiveBadgeuses(val)}
                            addSelection={(option) => {
                                attachUserBadgeuse(profile.id, option.value)
                                    .then(() => {
                                        setBadgeusesSelected([
                                            ...badgeusesSelected,
                                            option,
                                        ]);
                                        setIsActiveBadgeuses(false);
                                        refreshProfil?.();
                                        toast.success("Badgeuse associée.");
                                    })
                                    .catch((error) => {
                                        toast.error(
                                            error.response?.data?.message ||
                                                "Impossible d'associer la badgeuse."
                                        );
                                    });
                            }}
                            removeSelection={(option) => {
                                detachUserBadgeuse(profile.id, option.value)
                                    .then(() => {
                                        setBadgeusesSelected(
                                            badgeusesSelected.filter(
                                                (selected) =>
                                                    selected.value !== option.value
                                            )
                                        );
                                        setIsActiveBadgeuses(false);
                                        refreshProfil?.();
                                        toast.success("Badgeuse détachée.");
                                    })
                                    .catch((error) => {
                                        toast.error(
                                            error.response?.data?.message ||
                                                "Impossible de détacher la badgeuse."
                                        );
                                    });
                            }}
                        />
                    ) : (
                        <p className="text-sm text-gray-600">
                            {badgeusesSelected.length > 0
                                ? badgeusesSelected.map((b) => b.label).join(", ")
                                : "Aucune badgeuse associée."}
                        </p>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                <Section
                    title="Début de journée minimum"
                    description="Le planning réalisé ne peut pas commencer avant l'heure minimale."
                    ruleCode={RULE_CODES.START_MIN}
                    ruleValue={startMin}
                >
                    {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-6"> */}
                    <div className="flex flex-wrap gap-4">
                        <DefaultDropdown
                            name="Mode"
                            options={MODE_OPTIONS}
                            value={startMin.params?.mode || "fixed"}
                            onChange={(e) =>
                                updateRule(RULE_CODES.START_MIN, {
                                    params: { mode: e.target.value },
                                })
                            }
                        />
                        {startMin.params?.mode !== "provisional" && (
                            <InputForm
                                id="start-min-time"
                                type="time"
                                name="Heure minimale"
                                value={startMin.params?.time || "08:00"}
                                set={(v) =>
                                    updateRule(RULE_CODES.START_MIN, {
                                        params: { time: v },
                                    })
                                }
                                classNameWrapper="-mb-4"
                            />
                        )}
                    </div>
                </Section>

                <Section
                    title="Fin de journée maximum"
                    description="La fin du planning réalisé est tronquée à l'heure maximale."
                    ruleCode={RULE_CODES.END_MAX}
                    ruleValue={endMax}
                >
                    <div className="flex flex-wrap gap-4">
                        <DefaultDropdown
                            name="Mode"
                            options={MODE_OPTIONS}
                            value={endMax.params?.mode || "fixed"}
                            onChange={(e) =>
                                updateRule(RULE_CODES.END_MAX, {
                                    params: { mode: e.target.value },
                                })
                            }
                        />
                        {endMax.params?.mode !== "provisional" && (
                            <InputForm
                                id="end-max-time"
                                type="time"
                                name="Heure maximale"
                                value={endMax.params?.time || "20:00"}
                                set={(v) =>
                                    updateRule(RULE_CODES.END_MAX, {
                                        params: { time: v },
                                    })
                                }
                                classNameWrapper="-mb-4"
                            />
                        )}
                    </div>
                </Section>

                <Section
                    title="Amplitude maximale de la journée"
                    description="La fin de journée est tronquée à 'début + amplitude'."
                    ruleCode={RULE_CODES.MAX_AMPLITUDE}
                    ruleValue={amplitude}
                >
                    <div className="flex flex-wrap gap-4">
                        <InputForm
                            id="amplitude-duration"
                            type="time"
                            name="Amplitude (hh:mm)"
                            value={minutesToHhMm(amplitude.params?.minutes ?? 660)}
                            set={(v) =>
                                updateRule(RULE_CODES.MAX_AMPLITUDE, {
                                    params: { minutes: hhMmToMinutes(v) },
                                })
                            }
                            classNameWrapper="-mb-4"
                        />
                    </div>
                </Section>

                <Section
                    title="Pause repas obligatoire"
                    description="Une seule pause repas est appliquée par jour. Si aucune interruption n'est détectée dans la fenêtre cible, une pause par défaut est forcée."
                    ruleCode={RULE_CODES.MEAL_BREAK}
                    ruleValue={mealBreak}
                >
                    <div className="flex flex-wrap gap-4">
                        <DefaultDropdown
                            name="Mode"
                            options={MEAL_MODE_OPTIONS}
                            value={mealBreak.params?.mode || "fixed_window"}
                            onChange={(e) =>
                                updateRule(RULE_CODES.MEAL_BREAK, {
                                    params: { mode: e.target.value },
                                })
                            }
                        />

                        {mealBreak.params?.mode !== "provisional" && (
                            <>
                                <InputForm
                                    id="meal-window-start"
                                    type="time"
                                    name="Début fenêtre"
                                    value={mealBreak.params?.window_start || "12:00"}
                                    set={(v) =>
                                        updateRule(RULE_CODES.MEAL_BREAK, {
                                            params: { window_start: v },
                                        })
                                    }
                                    classNameWrapper="-mb-4"
                                />
                                <InputForm
                                    id="meal-window-end"
                                    type="time"
                                    name="Fin fenêtre"
                                    value={mealBreak.params?.window_end || "14:00"}
                                    set={(v) =>
                                        updateRule(RULE_CODES.MEAL_BREAK, {
                                            params: { window_end: v },
                                        })
                                    }
                                    classNameWrapper="-mb-4"
                                />
                                <div className="hidden lg:block" />
                            </>
                        )}

                        <InputForm
                            id="meal-min-duration"
                            type="time"
                            name="Durée minimale (hh:mm)"
                            value={minutesToHhMm(mealBreak.params?.min_minutes ?? 45)}
                            set={(v) =>
                                updateRule(RULE_CODES.MEAL_BREAK, {
                                    params: { min_minutes: hhMmToMinutes(v) },
                                })
                            }
                            classNameWrapper="-mb-4"
                        />
                        <InputForm
                            id="meal-default-duration"
                            type="time"
                            name="Durée par défaut (hh:mm)"
                            value={minutesToHhMm(mealBreak.params?.default_minutes ?? 60)}
                            set={(v) =>
                                updateRule(RULE_CODES.MEAL_BREAK, {
                                    params: { default_minutes: hhMmToMinutes(v) },
                                })
                            }
                            classNameWrapper="-mb-4"
                        />
                    </div>
                </Section>

                <div className="flex justify-end">
                    <Button
                        action={handleSave}
                        isVisible={editRight}
                        loading={saving}
                        name="Enregistrer les règles"
                        type="empty"
                    />
                </div>
            </div>
        </div>
    );
};

export default PointageTab;
